import React, { Component, useEffect } from 'react';
import { withStyles } from '@material-ui/core/styles';
import Panel from '../../components/Panel';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    Paper,
    Toolbar,
    Button,
    TableHead,
    TableRow,
    Typography,
    RadioGroup,
    FormControl,
    Radio,
    FormControlLabel,
    Dialog,
    DialogActions,
    DialogTitle,
    DialogContent,
    DialogContentText,
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { withRouter } from 'react-router-dom';
import { apiPost, apiGet } from '../../api';
import formSubmit from 'dom-form-submit';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import Snackbar from '@material-ui/core/Snackbar';
import Alert from '@material-ui/lab/Alert';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';

const styles = (theme) => ({
    fixedPanel: {
        [theme.breakpoints.up('md')]: {
            position: 'sticky',
            top: 120, // Matches the AppContent marginTop.
        },
    },
    actionBar: {
        paddingLeft: 0,
        paddingRight: 0,
        alignItems: 'flex-end',
    },
    spacer: {
        flexGrow: 1,
    },
    actionButton: {
        marginLeft: 5,
        marginRight: 5,
    },
    payMethod: {
        marginTop: 16,
        padding: 20,
    },
    heading: {
        fontWeight: 500,
    },
});

const cellPadding = 20;

// Default text shown above the Pay button when a tenant opts in via
// siteInfo.paymentRedirectMessage: true (see mapStateToProps in ./index.js).
const DEFAULT_PAYMENT_REDIRECT_MESSAGE =
    'Processing the payment may take some time. Please do not click the back button or close your browser until you are returned and see the confirmation.';

const LeftTableCell = withStyles(() => ({
    body: {
        paddingLeft: cellPadding,
        whiteSpace: 'normal',
        wordBreak: 'break-word',
    },
}))(TableCell);

const RightTableCell = withStyles(() => ({
    body: {
        paddingRight: cellPadding,
        textAlign: 'right',
    },
}))(TableCell);

/*
 * Style the header row
 */
const HeaderTableCell = withStyles((theme) => ({
    head: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
    },
    body: {
        fontSize: '14',
    },
}))(TableCell);

const LeftHeaderTableCell = withStyles(() => ({
    head: {
        paddingLeft: cellPadding,
    },
}))(HeaderTableCell);

const RightHeaderTableCell = withStyles(() => ({
    head: {
        paddingRight: cellPadding,
        textAlign: 'right',
    },
}))(HeaderTableCell);

/*
 * Style the fee total row
 */
const FeeTotalTableRow = withStyles((theme) => ({
    root: {
        backgroundColor: theme.palette.background.default,
    },
}))(TableRow);

const LeftFeeTotalTableCell = withStyles(() => ({
    body: {
        fontWeight: 'bold',
    },
}))(LeftTableCell);

const RightFeeTotalTableCell = withStyles(() => ({
    body: {
        fontWeight: 'bold',
    },
}))(RightTableCell);

const FeeTimer = () => {
    const siteInfo = useSelector(({ siteInfo }) => siteInfo);
    const timeOutMultiplier = siteInfo?.feeTimeOutMinutes || 60;
    const { enqueueSnackbar } = useSnackbar();
    useEffect(() => {
        setTimeout(() => {
            enqueueSnackbar('Payment Time Out', {
                variant: 'info',
                persist: true,
            }).then(
                setTimeout(() => {
                    window.location.href = '/';
                }, 6000),
            );
        }, 60000 * timeOutMultiplier);
    }, [enqueueSnackbar, timeOutMultiplier]);

    return (
        <Snackbar
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            open={alert.open}
            autoHideDuration={30000}
        >
            <Alert
                iconMapping={{ success: <CheckCircleIcon fontSize="inherit" /> }}
                variant="filled"
                severity={alert.type === 'Save' ? 'success' : 'info'}
                style={{ width: '70%' }}
            >
                {alert.message}
            </Alert>
        </Snackbar>
    );
};

class FeePanel extends Component {
    state = {
        fees: [],
        isLoading: true,
        paymentType: 'CreditCard',
        formPayLater: undefined,
        payLaterDialogOpen: false,
        // Guards against the Pay button being clicked (or double-clicked)
        // again while a previous click's handlePay() is still in flight,
        // which otherwise fires two independent POST /payments calls and
        // creates two payment records for the same application.
        isPaySubmitting: false,
    };

    async componentDidMount() {
        const authToken = await this.props.getAccessToken(this.props.authorizationScopes.applications);

        const { form } = (await apiGet(`applications/${this.props.applicationId}`, authToken)).data;

        this.setState({ formPayLater: form.payLater });

        try {
            const response = await apiGet(`applications/${this.props.applicationId}/fees`, authToken);

            this.setState({ fees: response.data, isLoading: false });
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);

            this.setState({ fees: undefined, isLoading: false });
        }
    }

    render() {
        const isAppStatusCompleted = async (token) => {
            token = token ?? (await this.props.getAccessToken(this.props.authorizationScopes.applications));

            const { status } = (
                await apiGet(`applications/${this.props.applicationId}`, token, null, {
                    filter: { fields: { status: true } },
                })
            ).data;

            return status === 'Completed';
        };

        const handlePay = async () => {
            // Guard against a second click (double-click, or an impatient
            // repeat click) firing this handler again while an earlier
            // click's request is still in flight -- each call below creates
            // an independent payment record for the same application, so
            // without this a fast double-click produces two payment rows.
            if (this.state.isPaySubmitting) {
                return;
            }

            this.setState({ isPaySubmitting: true });

            // Tracks whether we've committed to navigating away (either an
            // in-app route change or a redirect to the payment gateway).
            // formSubmit()/history.push() below don't navigate instantly --
            // there's a real, visible window before the browser actually
            // leaves this page -- so once we're in that window we must NOT
            // re-enable the button, or a click during that window fires a
            // second, independent handlePay() and creates a second payment.
            let isNavigatingAway = false;

            try {
                // If the status is not 'Completed' that means somehow the
                // application status flow was violated and the status is
                // not in the expected state.
                if (!(await isAppStatusCompleted())) {
                    this.props.setStatusErrorSnackbarOpen(true);

                    return;
                }

                const authToken = await this.props.getAccessToken(this.props.authorizationScopes.payments);

                const response = await apiPost(
                    'payments',
                    {
                        order: { items: [{ applicationId: this.props.applicationId }] },
                        paymentType: this.state.paymentType,
                    },
                    authToken,
                );

                /*  If payment service finds a successful payment in the gateway
                for this invoice, that was made through any previous payment attempts,
                then the service will return the following values in the transferData.
                */
                const result = response.data;
                const transferData = result.transferData;

                // eslint-disable-next-line no-console
                console.info('[handlePay] API response summary', {
                    paymentId: result?.id,
                    transferMethod: transferData?.transferMethod,
                    hasTransferUrl: !!transferData?.transferUrl,
                    hasParameters: !!transferData?.parameters,
                });

                if (transferData?.transferMethod === 'NO_TRANSFER' && result.id) {
                    this.props.history.push(`/payments/${result.id}`);
                    // Only mark navigating-away once the push call itself
                    // didn't throw, so a failure here still re-enables Pay.
                    isNavigatingAway = true;

                    return;
                }

                // eslint-disable-next-line no-console
                console.info('[handlePay] Redirecting to gateway', {
                    method: transferData?.transferMethod,
                    url: transferData?.transferUrl,
                });

                /* Else it is a submit transfer to the payment gateway site   ... */
                formSubmit(transferData.parameters, {
                    action: transferData.transferUrl,
                    method: transferData.transferMethod,
                });
                // Same reasoning as above: only suppress the finally-reset
                // once the redirect has actually been kicked off.
                isNavigatingAway = true;
            } finally {
                // Clear the guard on every path EXCEPT the ones where we're
                // navigating away -- there, the button must stay disabled
                // ("Processing...") for the remainder of this page's life,
                // since the browser hasn't actually left yet (see above).
                if (!isNavigatingAway) {
                    this.setState({ isPaySubmitting: false });
                }
            }
        };

        const handleSubmit = async () => {
            const authToken = await this.props.getAccessToken(this.props.authorizationScopes.applications);

            // If the status is not 'Completed' that means somehow the
            // application status flow was violated and the status is
            // not in the expected state.
            if (!(await isAppStatusCompleted(authToken))) {
                this.props.setStatusErrorSnackbarOpen(true);
            } else {
                apiPost(`applications/${this.props.applicationId}/submit`, null, authToken).then(() => {
                    this.props.history.push(`/applications/${this.props.applicationId}/submissionConfirmation`);
                });
            }

            this.setState({ payLaterDialogOpen: false });
        };

        const handleBack = async () => {
            this.props.history.push(`/applications/${this.props.applicationId}`, { resetStatus: true });
        };

        const handleChangePaymentMethod = (event) => {
            this.setState({ paymentType: event.target.value });
        };

        const payLaterDialog = (
            <Dialog open={this.state.payLaterDialogOpen}>
                <DialogTitle>Submit without paying?</DialogTitle>
                <DialogContent>
                    <DialogContentText id="payLaterConfirmation">
                        Proceeding without paying may require you to make arrangement for payment. Are you sure you
                        would like to continue?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => this.setState({ payLaterDialogOpen: false })} variant="contained">
                        No
                    </Button>
                    <Button onClick={handleSubmit} variant="contained" color="primary">
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>
        );

        let toolBar;
        let tableContainer;
        let noFeeMessage;
        let paymentMethod;
        let paymentRedirectNotice;
        let payNowDisabled = false;

        // handle error in fees
        if (!this.state.fees) {
            tableContainer = (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <LeftHeaderTableCell>Fee</LeftHeaderTableCell>
                                <RightHeaderTableCell>Amount</RightHeaderTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow>
                                <TableCell colSpan={2}>
                                    The application is unable to be submitted. Please contact customer service.
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            );

            toolBar = (
                <Toolbar className={this.props.classes.actionBar}>
                    <Button variant="contained" onClick={handleBack} className={this.props.classes.actionButton}>
                        Back
                    </Button>
                </Toolbar>
            );
            // set the fees
        } else {
            // if fees api has not returned, display a skeleton cell
            if (this.state.isLoading) {
                tableContainer = (
                    <TableContainer component={Paper}>
                        <Table>
                            <TableBody>
                                <TableRow>
                                    <TableCell colSpan={2}>
                                        <Skeleton />
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                );
            } else {
                // filter out fees that have no fee amount
                const fees = this.state.fees;

                // if there are no fees, display a message and a submit button instead of pay button
                if (!fees.length) {
                    noFeeMessage = 'You have no fees. Please review and submit your application.';

                    toolBar = (
                        <Toolbar className={this.props.classes.actionBar}>
                            <Button
                                variant="contained"
                                onClick={handleBack}
                                className={this.props.classes.actionButton}
                            >
                                Back
                            </Button>
                            <div className={this.props.classes.spacer} />
                            <Button
                                variant="contained"
                                onClick={handleSubmit}
                                color="primary"
                                className={this.props.classes.actionButton}
                                disabled={this.props.isStatusErrorSnackbarOpen}
                            >
                                Submit
                            </Button>
                        </Toolbar>
                    );
                } else {
                    const feeTotal = fees
                        .map((fee) => fee.amount)
                        .reduce((feeAmount1, feeAmount2) => feeAmount1 + feeAmount2);

                    const displayFees = fees.map((fee) => (
                        <TableRow key={fee.feeType}>
                            <LeftTableCell>{fee.description} </LeftTableCell>
                            <RightTableCell>
                                $
                                {fee.amount.toLocaleString('en-US', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                })}
                            </RightTableCell>
                        </TableRow>
                    ));

                    tableContainer = (
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <LeftHeaderTableCell>Fee</LeftHeaderTableCell>
                                        <RightHeaderTableCell>Amount</RightHeaderTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {displayFees}
                                    <FeeTotalTableRow>
                                        <LeftFeeTotalTableCell>Total</LeftFeeTotalTableCell>
                                        <RightFeeTotalTableCell>
                                            $
                                            {feeTotal.toLocaleString('en-US', {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2,
                                            })}
                                        </RightFeeTotalTableCell>
                                    </FeeTotalTableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    );

                    paymentMethod = this.props.siteInfo.selectPaymentType && (
                        <Paper className={this.props.classes.payMethod}>
                            <Typography className={this.props.classes.heading}>Select payment method</Typography>
                            <FormControl component="fieldset">
                                <RadioGroup
                                    aria-label="paymentMethod"
                                    name="radio-buttons-group"
                                    onChange={handleChangePaymentMethod}
                                    defaultValue="CreditCard"
                                >
                                    <FormControlLabel value="CreditCard" control={<Radio />} label="Credit Card" />
                                    <FormControlLabel value="eCheck" control={<Radio />} label="eCheck/ACH" />
                                </RadioGroup>
                            </FormControl>
                        </Paper>
                    );

                    const formPayLater = this.state.formPayLater;
                    const sitePayLater = this.props.siteInfo.payLater;

                    /*
                        If the form is configured to allow paying later the option should be available
                        and if it is explicitly configured to not allow paying later the option should
                        not be available. Otherwise, the tenant configuration should determine whether
                        the option should be available.

                        This gives full flexibility to either allow paying later option globally and
                        specific forms can be configured to exclude the option or to disallow paying
                        later globally and specific forms can be configured to include the option.
                     */
                    const payLaterEnabled = !!formPayLater || (formPayLater !== false && !!sitePayLater);

                    // If pay later button is enabled check whether pay button should
                    // be disabled.
                    if (payLaterEnabled) {
                        // If form disables pay now then disable it without looking at the site config.
                        if (typeof formPayLater === 'object' && formPayLater.disablePayNow) {
                            payNowDisabled = true;
                        } else if (typeof sitePayLater === 'object' && sitePayLater.disablePayNow) {
                            // If site disables pay now, only disable it if the form does not explicitly
                            // enable it.
                            if (typeof formPayLater !== 'object' || formPayLater.disablePayNow !== false) {
                                payNowDisabled = true;
                            }
                        }
                    }

                    const payButton = (
                        <Button
                            variant="contained"
                            onClick={handlePay}
                            color="primary"
                            className={this.props.classes.actionButton}
                            disabled={this.props.isStatusErrorSnackbarOpen || this.state.isPaySubmitting}
                        >
                            {this.state.isPaySubmitting ? 'Processing...' : 'Pay'}
                        </Button>
                    );

                    const payLaterButton = (
                        <Button
                            variant="contained"
                            // If pay now is disabled then submit without a warning dialog.
                            onClick={!payNowDisabled ? () => this.setState({ payLaterDialogOpen: true }) : handleSubmit}
                            className={this.props.classes.actionButton}
                            // If pay now is disabled then the pay later button color
                            // should be the primary color.
                            color={payNowDisabled ? 'primary' : undefined}
                            disabled={this.props.isStatusErrorSnackbarOpen}
                        >
                            Pay Later
                        </Button>
                    );

                    // siteInfo.paymentRedirectMessage (tenant config) is opt-in:
                    //  - not set / null -> show no message at all
                    //  - true           -> show the default message below
                    //  - a string       -> show that custom message instead
                    const paymentRedirectMessage = this.props.siteInfo.paymentRedirectMessage;
                    let paymentRedirectMessageText;

                    if (paymentRedirectMessage === true) {
                        paymentRedirectMessageText = DEFAULT_PAYMENT_REDIRECT_MESSAGE;
                    } else if (typeof paymentRedirectMessage === 'string' && paymentRedirectMessage) {
                        paymentRedirectMessageText = paymentRedirectMessage;
                    }

                    // Only warn about staying on the page when the Pay button (which redirects
                    // to the payment gateway and can take 30+ seconds to return) is available,
                    // and the tenant has opted in to the message.
                    if (!payNowDisabled && paymentRedirectMessageText) {
                        paymentRedirectNotice = (
                            <Alert severity="warning" style={{ marginBottom: 16 }}>
                                {paymentRedirectMessageText}
                            </Alert>
                        );
                    }

                    toolBar = (
                        <Toolbar className={this.props.classes.actionBar}>
                            <Button
                                variant="contained"
                                onClick={handleBack}
                                className={this.props.classes.actionButton}
                            >
                                Back
                            </Button>
                            <div className={this.props.classes.spacer} />
                            {payLaterEnabled && payLaterButton}
                            {!payNowDisabled && payButton}
                        </Toolbar>
                    );
                }
            }
        }

        return (
            <Panel className={this.props.classes.fixedPanel} title="Fees">
                {payLaterDialog}
                {tableContainer}
                {noFeeMessage}
                {!payNowDisabled && paymentMethod}
                {paymentRedirectNotice}
                {toolBar}
                <FeeTimer />
            </Panel>
        );
    }
}

export default withRouter(withStyles(styles)(FeePanel));
